import { useEffect, useRef, useState, useCallback, memo, Component } from 'react';
import Globe from 'react-globe.gl';
import * as THREE from 'three';
import type { Superteam, Country } from '../types';
import { getFlagImageUrl } from '../utils/flags';
import { isWebGLSupported } from '../utils/webgl';

// Error boundary to catch WebGL context creation failures (e.g. context limit, driver issues)
class WebGLErrorBoundary extends Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };
  static getDerivedStateFromError = () => ({ hasError: true });
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

export interface HoverPosition {
  x: number;
  y: number;
}

interface GlobeViewProps {
  superteams: Superteam[];
  countries: Country[];
  selectedSuperteam: Superteam | null;
  selectedCountry: Country | null;
  onSuperteamClick: (superteam: Superteam) => void;
  onSuperteamHover: (superteam: Superteam | null, position?: HoverPosition) => void;
  onCountryClick: (country: Country) => void;
  onCountryHover: (country: Country | null, position?: HoverPosition) => void;
}

// Solana brand colors
const SOLANA_COLORS = {
  primary: '#14F195',
  secondary: '#9945FF',
  accent: '#00C2FF',
};

// Heat signature colors based on member count
function getHeatColor(memberCount: number): string {
  if (memberCount <= 5) return '#14F195';   // Green - few
  if (memberCount <= 20) return '#FFC107';  // Yellow
  if (memberCount <= 50) return '#FF9800';  // Orange
  return '#EF4444';                          // Red - many
}

// Calculate sun position based on current time
// Returns { lat, lng } of the subsolar point (where sun is directly overhead)
function getSunPosition(): { lat: number; lng: number } {
  const now = new Date();
  
  // Day of year (0-365)
  const start = new Date(now.getFullYear(), 0, 0);
  const diff = now.getTime() - start.getTime();
  const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24));
  
  // Solar declination (latitude where sun is directly overhead)
  // Ranges from +23.45° (summer solstice) to -23.45° (winter solstice)
  const declination = -23.45 * Math.cos((360 / 365) * (dayOfYear + 10) * (Math.PI / 180));
  
  // Hour angle - sun longitude based on UTC time
  // At UTC 12:00, sun is at longitude 0°
  // Sun moves 15° per hour westward
  const utcHours = now.getUTCHours() + now.getUTCMinutes() / 60 + now.getUTCSeconds() / 3600;
  const sunLng = -((utcHours - 12) * 15);
  
  return { lat: declination, lng: sunLng };
}

// Fallback when WebGL is unavailable (VM, remote desktop, disabled GPU, etc.)
function GlobeFallback() {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-[#030014] via-[#0a0a2e] to-[#030014]">
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 360 180'%3E%3Cpath fill='%23fff' d='M0 90 Q90 60 180 90 T360 90 L360 180 L0 180 Z'/%3E%3C/svg%3E")`,
          backgroundSize: 'cover',
        }}
      />
      <div className="relative text-center px-6 max-w-sm">
        <p className="text-white/50 text-sm">
          The 3D globe isn&apos;t available in this browser. Explore Superteams using the menu below.
        </p>
      </div>
    </div>
  );
}

function GlobeView({ 
  superteams, 
  countries,
  selectedSuperteam, 
  selectedCountry,
  onSuperteamClick, 
  onSuperteamHover,
  onCountryClick,
  onCountryHover,
}: GlobeViewProps) {
  const globeRef = useRef<any>(null);
  const sunLightRef = useRef<THREE.DirectionalLight | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [webglAvailable, setWebglAvailable] = useState<boolean | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Only render on client
  useEffect(() => {
    setIsMounted(true);
    setWebglAvailable(isWebGLSupported());
  }, []);

  // Update sun position to create realistic day/night
  const updateSunLight = useCallback(() => {
    if (!sunLightRef.current) return;
    
    const sunPos = getSunPosition();
    
    // Convert lat/lng to 3D position (for directional light)
    const phi = (90 - sunPos.lat) * (Math.PI / 180);
    const theta = (sunPos.lng + 180) * (Math.PI / 180);
    const radius = 200;
    
    sunLightRef.current.position.set(
      radius * Math.sin(phi) * Math.cos(theta),
      radius * Math.cos(phi),
      radius * Math.sin(phi) * Math.sin(theta)
    );
  }, []);

  // Handle globe ready - configure controls and lighting
  const handleGlobeReady = useCallback(() => {
    if (globeRef.current) {
      try {
        const controls = globeRef.current.controls();
        if (controls) {
          controls.autoRotate = true;
          controls.autoRotateSpeed = 0.5;
          controls.enableZoom = true;
          controls.minDistance = 150;
          controls.maxDistance = 500;
        }

        // Access Three.js scene to add custom lighting
        const scene = globeRef.current.scene();
        if (scene) {
          // Remove default lights
          scene.children = scene.children.filter((child: any) => {
            if (child.isLight && !child.isAmbientLight) {
              return false;
            }
            return true;
          });

          // Add ambient light for the dark side (night)
          const ambientLight = new THREE.AmbientLight(0x222244, 0.4);
          scene.add(ambientLight);

          // Add directional light for the sun
          const sunLight = new THREE.DirectionalLight(0xffffff, 2.5);
          sunLight.castShadow = false;
          scene.add(sunLight);
          sunLightRef.current = sunLight;

          // Initial sun position
          updateSunLight();
        }
      } catch (e) {
        console.warn('Could not configure controls/lighting:', e);
      }
    }
  }, [updateSunLight]);

  // Update sun position every minute for real-time day/night
  useEffect(() => {
    if (!isMounted) return;
    
    const interval = setInterval(() => {
      updateSunLight();
    }, 60000); // Update every minute
    
    return () => clearInterval(interval);
  }, [isMounted, updateSunLight]);

  // Center globe on selected superteam or country
  useEffect(() => {
    if (!globeRef.current) return;
    
    const target = selectedSuperteam || selectedCountry;
    if (!target) return;

    try {
      // Disable auto-rotate when viewing a location
      const controls = globeRef.current.controls();
      if (controls) {
        controls.autoRotate = false;
      }

      // Animate to the selected location
      globeRef.current.pointOfView(
        { lat: target.lat, lng: target.lng, altitude: 1.8 },
        1200 // Animation duration
      );
    } catch (e) {
      console.warn('Could not animate globe:', e);
    }
  }, [selectedSuperteam, selectedCountry]);

  // Re-enable auto-rotate when no selection
  useEffect(() => {
    if (!globeRef.current || selectedSuperteam || selectedCountry) return;

    try {
      const controls = globeRef.current.controls();
      if (controls) {
        controls.autoRotate = true;
      }
    } catch (e) {
      console.warn('Could not re-enable auto-rotate:', e);
    }
  }, [selectedSuperteam, selectedCountry]);

  // Create unified markers array with both superteams and countries
  type MarkerType = 'superteam' | 'country';
  interface GlobeMarker {
    id: string;
    type: MarkerType;
    lat: number;
    lng: number;
    name: string;
    memberCount: number;
    // Superteam specific
    iconUrl?: string;
    country?: string;
    // Country specific
    flagEmoji?: string;
    code?: string;
  }

  // Filter out countries that already have superteams to avoid marker overlap
  // Users who represent a country with a superteam are auto-added to that superteam
  const superteamCountries = new Set(superteams.map(s => s.country.toLowerCase()));
  const filteredCountries = countries.filter(c => 
    // Only show countries with members that DON'T have a superteam
    c.memberCount > 0 && !superteamCountries.has(c.name.toLowerCase())
  );

  const superteamMarkers: GlobeMarker[] = superteams.map((team) => ({
    id: `superteam-${team.id}`,
    type: 'superteam' as const,
    lat: team.lat,
    lng: team.lng,
    name: team.name,
    memberCount: team.memberCount,
    iconUrl: team.iconUrl,
    country: team.country,
  }));

  const countryMarkers: GlobeMarker[] = filteredCountries
    .filter(c => c.memberCount > 0) // Only show countries with at least one member
    .map((country) => ({
      id: `country-${country.code}`,
      type: 'country' as const,
      lat: country.lat,
      lng: country.lng,
      name: country.name,
      memberCount: country.memberCount,
      flagEmoji: country.flagEmoji,
      code: country.code,
    }));

  const allMarkers = [...superteamMarkers, ...countryMarkers];

  if (!isMounted || webglAvailable === null) {
    return (
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/60">Loading globe...</p>
        </div>
      </div>
    );
  }

  if (webglAvailable === false) {
    return <GlobeFallback />;
  }

  return (
    <div ref={containerRef} className="absolute inset-0">
      <WebGLErrorBoundary fallback={<GlobeFallback />}>
        <Globe
        ref={globeRef}
        onGlobeReady={handleGlobeReady}
        globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
        bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
        backgroundImageUrl="//unpkg.com/three-globe/example/img/night-sky.png"
        backgroundColor="rgba(3, 0, 20, 0)"
        atmosphereColor="#a8c8ff"
        atmosphereAltitude={0.1}
        // HTML markers for interactive elements
        htmlElementsData={allMarkers}
        htmlLat="lat"
        htmlLng="lng"
        htmlAltitude={0.01}
        htmlElement={(d: any) => {
          const el = document.createElement('div');
          const isCountry = d.type === 'country';
          
          if (isCountry) {
            // Country marker - rectangular flag marker with heat glow
            el.className = 'country-marker';
            const isSelected = selectedCountry?.code === d.code;
            const heatColor = getHeatColor(d.memberCount);
            const flagWidth = 36;
            const flagHeight = 24; // 3:2 aspect ratio for flags
            
            // Outer glow ring (animated, color based on heat)
            const ring = document.createElement('div');
            ring.style.cssText = `
              position: absolute;
              width: ${flagWidth + 8}px;
              height: ${flagHeight + 8}px;
              border-radius: 6px;
              border: 2px solid ${heatColor};
              opacity: ${isSelected ? 0.9 : 0.6};
              animation: marker-pulse 2.5s ease-in-out infinite;
              transform: translate(-50%, -50%);
              top: 50%;
              left: 50%;
            `;
            el.appendChild(ring);
            
            // Flag rectangular container
            const flagContainer = document.createElement('div');
            flagContainer.style.cssText = `
              position: absolute;
              width: ${flagWidth}px;
              height: ${flagHeight}px;
              border-radius: 4px;
              overflow: hidden;
              cursor: pointer;
              transform: translate(-50%, -50%);
              top: 50%;
              left: 50%;
              transition: all 0.2s ease;
              box-shadow: 0 3px 15px rgba(0,0,0,0.5), 0 0 12px ${heatColor}88;
              border: 2px solid ${isSelected ? heatColor : 'rgba(255,255,255,0.3)'};
              background: rgba(0,0,0,0.5);
            `;
            
            // Flag image - using flagcdn.com for cross-platform support (Windows doesn't support flag emojis)
            const flagImg = document.createElement('img');
            flagImg.src = getFlagImageUrl(d.code || '');
            flagImg.alt = d.name;
            flagImg.style.cssText = `
              width: 100%;
              height: 100%;
              object-fit: cover;
            `;
            flagImg.onerror = () => {
              // Fallback to country code if image fails to load
              flagContainer.innerHTML = `<span style="font-size: 10px; font-weight: 600; color: white;">${d.code || '?'}</span>`;
              flagContainer.style.display = 'flex';
              flagContainer.style.alignItems = 'center';
              flagContainer.style.justifyContent = 'center';
            };
            flagContainer.appendChild(flagImg);
            el.appendChild(flagContainer);
            
            // Member count badge
            if (d.memberCount > 0) {
              const badge = document.createElement('div');
              badge.style.cssText = `
                position: absolute;
                top: calc(50% - ${flagHeight / 2 + 6}px);
                left: calc(50% + ${flagWidth / 2 - 4}px);
                transform: translateX(-50%);
                font-size: 9px;
                font-weight: 700;
                color: white;
                background: ${heatColor};
                padding: 1px 5px;
                border-radius: 8px;
                min-width: 16px;
                text-align: center;
                box-shadow: 0 2px 4px rgba(0,0,0,0.4);
              `;
              badge.textContent = String(d.memberCount);
              el.appendChild(badge);
            }
            
            // Style container
            el.style.cssText = `
              position: relative;
              width: ${flagWidth + 16}px;
              height: ${flagHeight + 20}px;
              pointer-events: auto;
              cursor: pointer;
            `;
            
            // Event handlers for country
            el.onclick = () => {
              const country = countries.find(c => c.code === d.code);
              if (country) onCountryClick(country);
            };
            el.onmouseenter = () => {
              flagContainer.style.transform = 'translate(-50%, -50%) scale(1.2)';
              flagContainer.style.boxShadow = `0 5px 20px rgba(0,0,0,0.6), 0 0 20px ${heatColor}`;
              flagContainer.style.borderColor = heatColor;
              const rect = el.getBoundingClientRect();
              const country = countries.find(c => c.code === d.code);
              if (country) onCountryHover(country, { x: rect.left + rect.width / 2, y: rect.top });
            };
            el.onmouseleave = () => {
              flagContainer.style.transform = 'translate(-50%, -50%) scale(1)';
              flagContainer.style.boxShadow = `0 3px 15px rgba(0,0,0,0.5), 0 0 12px ${heatColor}88`;
              flagContainer.style.borderColor = isSelected ? heatColor : 'rgba(255,255,255,0.3)';
              onCountryHover(null);
            };
          } else {
            // Superteam marker - square with icon
            el.className = 'superteam-marker';
            const superteamId = d.id.replace('superteam-', '');
            const isSelected = selectedSuperteam?.id === superteamId;
            const markerSize = 36;
            
            // Icon container with image
            const iconContainer = document.createElement('div');
            iconContainer.style.cssText = `
              position: absolute;
              width: ${markerSize}px;
              height: ${markerSize}px;
              border-radius: 8px;
              overflow: hidden;
              cursor: pointer;
              transform: translate(-50%, -50%);
              top: 50%;
              left: 50%;
              transition: all 0.2s ease;
              box-shadow: 0 3px 12px rgba(0,0,0,0.5);
              border: 2px solid ${isSelected ? SOLANA_COLORS.secondary : 'rgba(255,255,255,0.3)'};
            `;
            
            // Superteam icon image
            if (d.iconUrl) {
              const img = document.createElement('img');
              img.src = d.iconUrl;
              img.alt = d.name;
              img.style.cssText = `
                width: 100%;
                height: 100%;
                object-fit: cover;
              `;
              iconContainer.appendChild(img);
            } else {
              // Fallback gradient background
              iconContainer.style.background = `linear-gradient(135deg, ${SOLANA_COLORS.primary}, ${SOLANA_COLORS.secondary})`;
              const fallbackText = document.createElement('div');
              fallbackText.textContent = (d.country || 'XX').substring(0, 2).toUpperCase();
              fallbackText.style.cssText = `
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 16px;
                font-weight: bold;
                color: white;
              `;
              iconContainer.appendChild(fallbackText);
            }
            el.appendChild(iconContainer);
            
            // Country label below
            const label = document.createElement('div');
            label.style.cssText = `
              position: absolute;
              top: calc(50% + ${markerSize / 2 + 6}px);
              left: 50%;
              transform: translateX(-50%);
              font-size: 11px;
              font-weight: 600;
              color: white;
              text-shadow: 0 2px 4px rgba(0,0,0,0.9);
              white-space: nowrap;
              pointer-events: none;
              background: rgba(0,0,0,0.6);
              padding: 2px 8px;
              border-radius: 4px;
            `;
            label.textContent = d.country || d.name;
            el.appendChild(label);
            
            // Style container
            el.style.cssText = `
              position: relative;
              width: ${markerSize + 20}px;
              height: ${markerSize + 35}px;
              pointer-events: auto;
              cursor: pointer;
            `;
            
            // Event handlers for superteam
            el.onclick = () => {
              const superteam = superteams.find(s => s.id === superteamId);
              if (superteam) onSuperteamClick(superteam);
            };
            el.onmouseenter = () => {
              iconContainer.style.transform = 'translate(-50%, -50%) scale(1.15)';
              iconContainer.style.boxShadow = `0 6px 25px rgba(0,0,0,0.6), 0 0 25px ${SOLANA_COLORS.primary}`;
              iconContainer.style.borderColor = SOLANA_COLORS.primary;
              const rect = el.getBoundingClientRect();
              const superteam = superteams.find(s => s.id === superteamId);
              if (superteam) onSuperteamHover(superteam, { x: rect.left + rect.width / 2, y: rect.top });
            };
            el.onmouseleave = () => {
              iconContainer.style.transform = 'translate(-50%, -50%) scale(1)';
              iconContainer.style.boxShadow = '0 3px 12px rgba(0,0,0,0.5)';
              iconContainer.style.borderColor = isSelected ? SOLANA_COLORS.secondary : 'rgba(255,255,255,0.3)';
              onSuperteamHover(null);
            };
          }
          
          return el;
        }}
        // Arcs between connected superteams (optional visual flair)
        arcsData={[]}
      />
      </WebGLErrorBoundary>
      
      {/* CSS for marker animation */}
      <style>{`
        @keyframes marker-pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.4; }
          50% { transform: translate(-50%, -50%) scale(1.3); opacity: 0.2; }
        }
      `}</style>
    </div>
  );
}

export default memo(GlobeView);
